'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { FaBolt, FaAward, FaBus, FaShieldAlt } from 'react-icons/fa';

const WhyChooseSection = () => {
  const sectionStyle = {
    position: 'relative',
    padding: '5rem 1rem',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #f9fafb 0%, #eef2ff 100%)',
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
  };

  const headingWrapStyle = {
    textAlign: 'center',
    marginBottom: '3.5rem',
  };

  const titleStyle = {
    fontSize: 'clamp(1.9rem, 3vw, 2.3rem)',
    fontWeight: 900,
    color: '#0f172a',
    letterSpacing: '-0.03em',
    lineHeight: 1.2,
    margin: 0,
  };

  const primaryBlue = '#005884';

  const subtitleStyle = {
    marginTop: '1rem',
    fontSize: '0.98rem',
    maxWidth: '780px',
    marginLeft: 'auto',
    marginRight: 'auto',
    color: '#4b5563',
    lineHeight: 1.8,
  };

  const accentBarWrapStyle = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1.25rem',
    gap: '0.5rem',
  };

  const accentBarMain = {
    width: '42px',
    height: '3px',
    borderRadius: '999px',
    background: primaryBlue,
  };

  const accentBarSmall = (color) => ({
    width: '12px',
    height: '3px',
    borderRadius: '999px',
    background: color,
  });

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '1.75rem',
  };

  const gridStyleMobile = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '1.5rem',
  };

  const cardBase = {
    background: '#ffffff',
    borderRadius: '1rem',
    padding: '1.5rem 1.4rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem',
    alignItems: 'flex-start',
    transition: 'all 0.25s ease',
    outline: 'none',
  };

  const iconWrap = (bg, color) => ({
    width: '2.75rem',
    height: '2.75rem',
    borderRadius: '999px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: bg,
    color,
    fontSize: '1.25rem',
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
  });

  const cardTitle = {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  };

  const cardText = {
    fontSize: '0.9rem',
    color: '#6b7280',
    lineHeight: 1.7,
    margin: 0,
  };

  const featureContentWrap = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const cardVariants = (delay = 0) => ({
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, delay },
    },
  });

  // decide mobile vs desktop grid at render time
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const appliedGrid = isMobile ? gridStyleMobile : gridStyle;

  return (
    <section
      style={sectionStyle}
      aria-labelledby="why-choose-title"
      role="region"
      aria-label="Why Choose Us"
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '-120px',
          background:
            'radial-gradient(circle at top left, rgba(59,130,246,0.09), transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: '-80px',
          bottom: '-80px',
          width: '220px',
          height: '220px',
          borderRadius: '999px',
          border: '1px dashed rgba(148,163,184,0.45)',
        }}
      />

      <div style={containerStyle}>
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          variants={sectionVariants}
          style={headingWrapStyle}
        >
          <h2 style={titleStyle} id="why-choose-title">
            Why Choose{' '}
            <span style={{ color: primaryBlue }}>Abraj Al Dhafna</span> 
          </h2>
          <p style={subtitleStyle}>
            Leading provider of skilled manpower supply and construction subcontracting in Qatar.
            Certified tradesmen, complete project execution for civil, MEP, and finishing works. ISO
            certified. Serving Doha, Lusail, The Pearl, West Bay, and all major project sites.
          </p>
          <div style={accentBarWrapStyle}>
            <div style={accentBarMain} />
            <div style={accentBarSmall('#0ea5e9')} />
            <div style={accentBarSmall('#22c55e')} />
          </div>
        </motion.div>

        <div style={appliedGrid}>
          {/* Card 1 */}
          <motion.div
            tabIndex={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={cardVariants(0)}
            style={cardBase}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow =
                '0 16px 35px rgba(37, 99, 235, 0.14)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow =
                '0 8px 24px rgba(15, 23, 42, 0.04)';
            }}
          >
            <div
              style={iconWrap('rgba(37,99,235,0.09)', '#2563eb')}
              aria-label="24/7 Manpower"
            >
              <FaBolt />
            </div>
            <div style={featureContentWrap}>

              <h4 style={cardTitle}>Expert Team:</h4>
              <p style={cardText}>
               Our skilled professionals bring extensive experience and technical expertise to every project, ensuring quality workmanship and adherence to industry standards.
              </p>
            </div>
          </motion.div>

          {/* Card 2 */}
          <motion.div
            tabIndex={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={cardVariants(0.05)}
            style={cardBase}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow =
                '0 16px 35px rgba(234,179,8,0.14)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow =
                '0 8px 24px rgba(15, 23, 42, 0.04)';
            }}
          >
            <div
              style={iconWrap('rgba(234,179,8,0.12)', '#eab308')}
              aria-label="ISO Certified"
            >
              <FaAward />
            </div>
            <div style={featureContentWrap}>
              <h4 style={cardTitle}>Innovative Solutions:</h4>
              <p style={cardText}>
                We leverage cutting-edge technology and innovative practices to provide efficient and sustainable MEP systems that meet the evolving needs of our clients.
              </p>
            </div>
          </motion.div>

          {/* Card 3 */}
          <motion.div
            tabIndex={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={cardVariants(0.1)}
            style={cardBase}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow =
                '0 16px 35px rgba(16,185,129,0.14)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow =
                '0 8px 24px rgba(15, 23, 42, 0.04)';
            }}
          >
            <div
              style={iconWrap('rgba(16,185,129,0.12)', '#10b981')}
              aria-label="Staff Transportation"
            >
              <FaBus />
            </div>
            <div style={featureContentWrap}>
              <h4 style={cardTitle}>Customer-Centric Approach:</h4>
              <p style={cardText}>
                We prioritize our clients' needs and work collaboratively to deliver customized solutions that align with their objectives and timelines.

              </p>
            </div>
          </motion.div>

          {/* Card 4 */}
          <motion.div
            tabIndex={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={cardVariants(0.15)}
            style={cardBase}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow =
                '0 16px 35px rgba(244,63,94,0.14)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow =
                '0 8px 24px rgba(15, 23, 42, 0.04)';
            }}
          >
            <div
              style={iconWrap('rgba(244,63,94,0.12)', '#f43f5e')}
              aria-label="Safety Compliant"
            >
              <FaShieldAlt />
            </div>
            <div style={featureContentWrap}>
              <h4 style={cardTitle}>Commitment to Safety:</h4>
              <p style={cardText}>
                Safety is paramount in all our operations. We adhere to strict safety protocols and regulations to protect our workforce, clients, and the environment.

              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
